import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, ArrowRight, Lock } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Reader-facing "Action Required" surface on the Dashboard — one row
 * per project where the current user has a manuscript_acknowledgments
 * row with state="pending". Powered by GET /api/jobs/me/confidentiality-pending.
 *
 * Hidden entirely when the user has no pending rows (so the card never
 * steals real-estate from the dashboard). Dual-role users (author +
 * beta_reader) still see this because the backend scopes by user_id,
 * not by role.
 */
export default function ActionRequiredConfidentialityCard() {
  const [pending, setPending] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/jobs/me/confidentiality-pending");
        if (alive) setPending(data.pending || []);
      } catch {
        if (alive) setPending([]);
      }
    })();
    return () => { alive = false; };
  }, []);
  if (!pending || pending.length === 0) return null;
  return (
    <div
      className="mt-6 rounded-2xl border-2 border-[hsl(var(--sb-clay))]/60 bg-[hsl(var(--sb-clay))]/8 shadow-md"
      data-testid="dashboard-action-required-confidentiality"
    >
      <div className="p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <div className="shrink-0 inline-flex items-center justify-center h-10 w-10 rounded-full bg-[hsl(var(--sb-clay))]/20 text-[hsl(var(--sb-clay-2))]">
            <ShieldAlert size={20} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-[hsl(var(--sb-clay-2))]">
              Action required
            </div>
            <h2 className="font-serif text-2xl mt-0.5">
              {pending.length === 1
                ? "Review confidentiality before reading"
                : `Review confidentiality on ${pending.length} projects`}
            </h2>
            <p className="mt-1.5 text-sm text-muted-foreground">
              {pending.every((p) => p.update_required)
                ? "The confidentiality agreement was updated to explicitly prohibit AI-generated beta feedback and sharing manuscript content with AI tools or services. Accept the new version to keep reading."
                : "Your application was accepted. One step remains before manuscript access is available."}
            </p>
          </div>
        </div>
        <ul className="mt-4 space-y-2.5">
          {pending.map((p) => (
            <li
              key={p.job_id}
              className="rounded-xl border border-[hsl(var(--sb-clay))]/30 bg-background p-3.5 flex items-center justify-between gap-3 flex-wrap"
              data-testid={`dashboard-action-required-row-${p.job_id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium truncate">{p.job_title || "Untitled project"}</span>
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  <span
                    className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--sb-forest))]/40 bg-[hsl(var(--sb-moss))]/40 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-[hsl(var(--sb-forest-2))]"
                    data-testid={`dashboard-action-badge-application-${p.job_id}`}
                  >
                    Application accepted
                  </span>
                  <span
                    className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--sb-clay))]/40 bg-[hsl(var(--sb-clay))]/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-[hsl(var(--sb-clay-2))]"
                    data-testid={`dashboard-action-badge-confidentiality-${p.job_id}`}
                  >
                    <ShieldAlert size={10} /> {p.update_required ? `Agreement updated · v${p.required_version}` : "Confidentiality pending"}
                  </span>
                  <span
                    className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/60 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground"
                    data-testid={`dashboard-action-badge-manuscript-${p.job_id}`}
                  >
                    <Lock size={10} /> Manuscript locked
                  </span>
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  by {p.author_name || "the author"}
                  {p.update_required && p.previously_accepted_version && <> · you accepted v{p.previously_accepted_version}</>}
                  {!p.update_required && p.pending_since && (
                    <> · waiting since {new Date(p.pending_since).toLocaleDateString()}</>
                  )}
                </div>
              </div>
              <Link
                to={p.link || `/jobs/${p.job_id}#confidentiality`}
                className="btn-primary whitespace-nowrap"
                data-testid={`dashboard-action-required-cta-${p.job_id}`}
              >
                {p.update_required ? "Review update" : "Review confidentiality"} <ArrowRight size={14} />
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
